/**
 * MejoraRepo sobre Supabase. Misma lógica que DexieMejoraRepo (feedback PMIS).
 */

import type { MejoraPmis } from "@app-types/entities";
import type { MejoraRepo, RespuestaAdminInput } from "@core/data/repos/MejoraRepo";
import { calcularFechaCierre } from "@core/data/repos/MejoraRepo";
import { SupabaseBaseRepo } from "./SupabaseBaseRepo";

export class SupabaseMejoraRepo extends SupabaseBaseRepo<MejoraPmis> implements MejoraRepo {
  constructor(tenantId: string) {
    super("mejora", tenantId);
  }

  async list(): Promise<MejoraPmis[]> {
    return (await this.listAll()).sort((a, b) => b.fechaReporte.localeCompare(a.fechaReporte));
  }

  async get(id: string): Promise<MejoraPmis | null> {
    return this.getById(id);
  }

  async create(
    data: Omit<
      MejoraPmis,
      "id" | "tenantId" | "createdAt" | "updatedAt" | "version" | "createdBy" | "updatedBy" | "estado" | "respuestaAdmin" | "fechaRespuesta" | "respondidoPor"
    >,
    userEmail: string,
  ): Promise<string> {
    const id = crypto.randomUUID();
    const now = new Date().toISOString();
    const entry: MejoraPmis = {
      id,
      tenantId: this.tenantId,
      hallazgo: data.hallazgo,
      modulo: data.modulo,
      fechaReporte: data.fechaReporte,
      reportadoPor: data.reportadoPor,
      reportadoPorNombre: data.reportadoPorNombre,
      reportadoPorRol: data.reportadoPorRol,
      ...(data.reportadoPorGerencia ? { reportadoPorGerencia: data.reportadoPorGerencia } : {}),
      estado: "ABIERTO",
      createdAt: now,
      updatedAt: now,
      createdBy: userEmail,
      updatedBy: userEmail,
      version: 1,
    };
    await this.createRaw(entry);
    return id;
  }

  async responder(id: string, input: RespuestaAdminInput, adminEmail: string): Promise<void> {
    const actual = await this.get(id);
    if (!actual) throw new Error("Mejora no encontrada");
    const now = new Date().toISOString();
    const { fechaCierre: cierrePrevio, ...base } = actual;
    const fechaCierre = calcularFechaCierre(input.estado, cierrePrevio, now.slice(0, 10));
    const updated: MejoraPmis = {
      ...base,
      respuestaAdmin: input.respuestaAdmin,
      fechaRespuesta: now.slice(0, 10),
      respondidoPor: adminEmail,
      estado: input.estado,
      ...(fechaCierre !== undefined && { fechaCierre }),
      updatedAt: now,
      updatedBy: adminEmail,
      version: actual.version + 1,
    };
    await this.putRaw(updated);
  }

  async delete(id: string): Promise<void> {
    await this.deleteById(id);
  }
}
